/**
 * MakerPanel — the maker's side of the profile: the cards you put here, each with where it
 * stands, the numbers the host measured (card_stats, migration 20260930090000: how many phones
 * opened it, how many stayed, how many pressed on — never what the page said about itself, never
 * likes) and Take down (yours to remove, the same day, no questions) while it is live or
 * waiting, and the cards you liked, each a way back to it. What you took down leaves the list
 * and sits behind one line, "N taken down · Show", folded until asked (the founder's ruling,
 * 27 Sep: the list is what is up and waiting, not everything you ever tried; a card the reviewer
 * hid stays, since its reason is feedback). Shared by the profile page (/you,
 * ProfilePage.tsx) and the account sheet. Imports nothing that emits a row on load
 * (tests/unit/join.test.ts pins the list); showing the numbers writes one stats_open row, the
 * last step of the creation loop (docs/SPRINT-2.md section 2).
 */
import { For, Show, createResource, createSignal } from 'solid-js';
import { cardStanding, cardStats, isTakenDown, logStatsOpen, myCards, removeOwnCard, statsSentence } from '../api/cards';
import { myLikes } from '../api/likes';
import type { GameMeta } from '../lib/protocol';

export function MakerPanel(props: { onGoTo: (id: string) => void; onRemoved: (id: string) => void; toast: (m: string) => void }) {
  const [mine, { refetch }] = createResource(() => myCards());
  const [stats] = createResource(async () => {
    const m = await cardStats(14);
    if (m.size > 0) logStatsOpen();
    return m;
  });
  const [liked, likedActions] = createResource(async () => (await myLikes()) ?? []);
  const [busy, setBusy] = createSignal<string | null>(null);
  const [showDown, setShowDown] = createSignal(false);
  /** The list a maker reads: up, waiting, or hidden by the reviewer. What they took down is folded below it. */
  const shown = () => (mine() ?? []).filter((c) => !isTakenDown(c));
  const down = () => (mine() ?? []).filter(isTakenDown);
  const remove = async (m: GameMeta) => {
    if (!window.confirm(`Take "${m.title}" down? It leaves the feed for everyone. Adding the same address again puts it back on the bench.`)) return;
    setBusy(m.id);
    const r = await removeOwnCard(m);
    setBusy(null);
    if (!r.ok) {
      props.toast(r.reason);
      return;
    }
    props.onRemoved(m.id);
    props.toast('Taken down.');
    void refetch();
    void likedActions.refetch();
  };
  return (
    <div class="maker" data-testid="maker-panel">
      <h4>Your cards</h4>
      <Show when={mine()} fallback={<p class="hint">Loading…</p>}>
        {(list) => (
          <Show when={list().length} fallback={<p class="hint">Nothing yet. Press + to link a site or upload a page, a photo or a clip.</p>}>
            <Show when={shown().length} fallback={<p class="hint" data-testid="my-cards-none-up">Nothing of yours is up right now.</p>}>
            <ul class="maker-list" data-testid="my-cards">
              <For each={shown()}>
                {(c) => {
                  const m = c.meta;
                  const open = c.status === 'live' || c.status === 'bench';
                  return (
                    <li data-status={c.status}>
                      <Show when={open} fallback={<span class="t">{m.title}</span>}>
                        <button type="button" class="t" onClick={() => props.onGoTo(m.id)} title="Open it in the feed">
                          {m.title}
                        </button>
                      </Show>
                      <span class="by" data-testid="my-card-status">
                        {cardStanding(c)}
                      </span>
                      <Show when={open}>
                        <button type="button" class="btn danger" disabled={busy() === m.id} onClick={() => void remove(m)} data-testid="remove-card">
                          {busy() === m.id ? 'Removing…' : 'Take down'}
                        </button>
                      </Show>
                      <Show when={stats()?.get(m.id)}>{(s) => <span class="numbers" data-testid="my-card-numbers">{statsSentence(s())}</span>}</Show>
                    </li>
                  );
                }}
              </For>
            </ul>
            </Show>
            <Show when={(stats()?.size ?? 0) > 0}>
              <p class="hint" data-testid="my-cards-note">
                Measured by Blitz over the last 14 days: phones that opened it, how many stayed five seconds or more, how many pressed on. Your own looks on this device are left out; another device of yours counts as a stranger.
              </p>
            </Show>
            <Show when={down().length}>
              <p class="hint links">
                <button type="button" class="linkish quiet" onClick={() => setShowDown(!showDown())} aria-expanded={showDown()} data-testid="my-cards-down-toggle">
                  {down().length === 1 ? '1 taken down' : `${down().length} taken down`} · {showDown() ? 'Hide' : 'Show'}
                </button>
              </p>
              <Show when={showDown()}>
                <ul class="maker-list down" data-testid="my-cards-down">
                  <For each={down()}>
                    {(c) => (
                      <li data-status={c.status}>
                        <span class="t">{c.meta.title}</span>
                        <span class="by">{cardStanding(c)}</span>
                      </li>
                    )}
                  </For>
                </ul>
                <p class="hint" data-testid="my-cards-down-note">
                  Taken down by you. Adding the same address again puts it back on the bench.
                </p>
              </Show>
            </Show>
          </Show>
        )}
      </Show>
      <h4>Liked</h4>
      <Show when={liked()} fallback={<p class="hint">Loading…</p>}>
        {(list) => (
          <Show when={list().length} fallback={<p class="hint">Tap the heart on a card and it is kept here.</p>}>
            <ul class="maker-list" data-testid="my-likes">
              <For each={list()}>
                {(m) => (
                  <li>
                    <button type="button" class="t" onClick={() => props.onGoTo(m.id)} title="Open it in the feed">
                      {m.title}
                    </button>
                    <span class="by">@{m.developer.replace(/s+/g, '').toLowerCase()}</span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        )}
      </Show>
    </div>
  );
}

